import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  entrar as entrarNoCognito,
  sair as sairDoCognito,
  sessaoAtual,
  type OperadorLogado,
  type ResultadoDoLogin,
} from './cognito.js';

interface Sessao {
  operador: OperadorLogado | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<ResultadoDoLogin>;
  confirmarEntrada: (operador: OperadorLogado) => void;
  sair: () => void;
  temPapel: (...papeis: string[]) => boolean;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorDeSessao({ children }: { children: ReactNode }) {
  const [operador, setOperador] = useState<OperadorLogado | null>(null);
  const [carregando, setCarregando] = useState(true);

  // Ao abrir o cockpit, tenta restaurar a sessão guardada pelo Cognito.
  useEffect(() => {
    let ativo = true;
    sessaoAtual()
      .then((sessao) => {
        if (ativo) setOperador(sessao?.operador ?? null);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const resultado = await entrarNoCognito(email, senha);
    if (resultado.tipo === 'entrou') setOperador(resultado.operador);
    return resultado;
  }, []);

  const sair = useCallback(() => {
    sairDoCognito();
    setOperador(null);
  }, []);

  const temPapel = useCallback(
    (...papeis: string[]) =>
      !!operador &&
      (operador.papeis.includes('admin') || papeis.some((p) => operador.papeis.includes(p))),
    [operador],
  );

  const valor = useMemo<Sessao>(
    () => ({ operador, carregando, entrar, confirmarEntrada: setOperador, sair, temPapel }),
    [operador, carregando, entrar, sair, temPapel],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): Sessao {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useSessao precisa estar dentro de <ProvedorDeSessao>.');
  return contexto;
}
